import type { LedgerCoverage, Transaction } from "../types";
import { matchesCategory, transactionAllocations } from "./allocations";
import { isExpense, isIncome, isSpendable } from "./finance";

// What a windowed payload says about the older entries so the screens that only need these answers
// do not need the entries: built on the server over the whole ledger, and by the client over what it
// holds when a payload has no coverage (see `LedgerCoverage`).

export type LedgerDigests = Pick<LedgerCoverage,
  "uncategorizedSince" | "payeeCounts" | "payeeSince" | "tripSince" | "tripTitles" | "accountSince" | "iouSince" | "billSince" | "categoryUse" | "allocationCategories">;

export const isUncategorizedExpense = (t: Transaction) => isSpendable(t) && matchesCategory(t, "Uncategorized");

/** The "YYYY-MM" of an entry. */
const monthOf = (t: Transaction) => t.date.slice(0, 7);

/** The month of the oldest entry for each key `keyOf` names; entries it gives no key are skipped. */
function oldestMonthBy(transactions: Transaction[], keyOf: (t: Transaction) => string | undefined): Record<string, string> {
  const oldest: Record<string, string> = {};
  for (const t of transactions) {
    const key = keyOf(t);
    if (!key) continue;
    const month = monthOf(t);
    if (!oldest[key] || month < oldest[key]) oldest[key] = month;
  }
  return oldest;
}

/** How many entries carry each key `keyOf` names. */
function countBy(transactions: Transaction[], keyOf: (t: Transaction) => string | undefined): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const t of transactions) {
    const key = keyOf(t);
    if (key) counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/** An account's detail shows its newest entries; this many are enough to fill it. */
export const ACCOUNT_RECENT = 5;

/**
 * Per account: the month of the newest entries' tail — the month reached by the `ACCOUNT_RECENT`th
 * entry, or the oldest one when there are fewer. The ledger is newest first, so loading back to it
 * loads everything the account's detail lists. An account with no entries is absent.
 */
export function accountRecentSince(transactions: Transaction[]): Record<string, string> {
  const seen: Record<string, number> = {};
  const since: Record<string, string> = {};
  for (const t of transactions) {
    for (const id of new Set([t.accountId, t.toAccountId])) {
      if (!id || (seen[id] ?? 0) >= ACCOUNT_RECENT) continue;
      seen[id] = (seen[id] ?? 0) + 1;
      since[id] = monthOf(t);
    }
  }
  return since;
}

/** The category and subcategory pairs entries use, as the keys the category settings screen looks up. */
export function categoryUseKeys(transactions: Transaction[]): string[] {
  // Most entries repeat a handful of pairs, so the cheap string key is what runs per entry and the
  // JSON key is written once per distinct pair.
  const pairs = new Map<string, [string, string, string]>();
  for (const t of transactions) {
    const kind = isExpense(t) ? "expense" : isIncome(t) ? "income" : "";
    if (!kind) continue;
    for (const p of transactionAllocations(t))
      pairs.set(`${kind}\u0000${p.category}\u0000${p.subcategory}`, [kind, p.category, p.subcategory]);
  }
  const keys = new Set<string>();
  for (const [kind, category, subcategory] of pairs.values()) {
    keys.add(JSON.stringify([kind, category]));
    keys.add(JSON.stringify([kind, category, subcategory]));
  }
  return [...keys].sort();
}

/** The month of the oldest uncategorised expense, or empty when there is none. */
export function uncategorizedSince(transactions: Transaction[]): string {
  let oldest = "";
  for (const t of transactions) if (isUncategorizedExpense(t) && (!oldest || monthOf(t) < oldest)) oldest = monthOf(t);
  return oldest;
}

/** Per payee: how many entries link to it and the month of its oldest. */
export const payeeDigest = (transactions: Transaction[]) => ({
  payeeCounts: countBy(transactions, (t) => t.payeeId),
  payeeSince: oldestMonthBy(transactions, (t) => t.payeeId),
});

/** Per trip: the month of its oldest entry and the titles on its entries. */
export function tripDigest(transactions: Transaction[]): Pick<LedgerDigests, "tripSince" | "tripTitles"> {
  const titles: Record<string, Set<string>> = {};
  for (const t of transactions) if (t.tripId && t.title) (titles[t.tripId] ??= new Set()).add(t.title);
  return {
    tripSince: oldestMonthBy(transactions, (t) => t.tripId),
    tripTitles: Object.fromEntries(Object.entries(titles).map(([id, set]) => [id, [...set].sort()])),
  };
}

/** Every category any entry's allocation uses, sorted. */
export function allocationCategoryList(transactions: Transaction[]): string[] {
  const seen = new Set<string>();
  for (const t of transactions) for (const p of transactionAllocations(t)) seen.add(p.category);
  return [...seen].sort();
}

/** Everything a windowed payload carries about the entries it left out, over the whole ledger. */
export function ledgerDigests(transactions: Transaction[]): LedgerDigests {
  return {
    uncategorizedSince: uncategorizedSince(transactions),
    ...payeeDigest(transactions),
    ...tripDigest(transactions),
    accountSince: accountRecentSince(transactions),
    iouSince: oldestMonthBy(transactions, (t) => t.iouId),
    billSince: oldestMonthBy(transactions, (t) => t.billId),
    categoryUse: categoryUseKeys(transactions),
    allocationCategories: allocationCategoryList(transactions),
  };
}
